/**
 * Settings.
 *
 * Two views of one set of values. The tab holds the handful worth changing mid
 * game; the full panel holds everything, grouped by what a player is trying to
 * fix: how it looks, how it plays, how it is controlled.
 */

import { useEffect, useState } from 'react';
import type { GameSettings, TouchMode } from '../game/camera';
import { useDraggableWindow } from './draggable';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';
import { CloseButton } from './CloseButton';
import { LayoutSettings } from './LayoutSettings';
import {
  bareKeyOf,
  HELD_ACTIONS,
  KEY_GROUPS,
  keyOf,
  readable,
  slotOf,
  type KeyBindings,
} from './keys';
import { Explain } from './Explain';

type Update = (patch: Partial<GameSettings>) => void;

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}): JSX.Element {
  return (
    <div className="setting">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}): JSX.Element {
  return (
    <div className="setting">
      <span>{label}</span>
      <input type="checkbox" checked={value} onChange={(event) => onChange(event.target.checked)} />
    </div>
  );
}

const percent = (value: number): string => `${Math.round(value * 100)}%`;

function Slider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}): JSX.Element {
  return (
    <div className="setting">
      <span>{label}</span>
      <span className="setting-slider">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span className="setting-value">{format(value)}</span>
      </span>
    </div>
  );
}

/** When a roof is taken away, so what is under it can be seen. */
const ROOF_OPTIONS = [
  { value: 'underneath' as const, label: 'Inside' },
  { value: 'hovered' as const, label: 'Under the pointer' },
  { value: 'both' as const, label: 'Both' },
  { value: 'always' as const, label: 'Always' },
];

const TOUCH_OPTIONS: { value: TouchMode; label: string }[] = [
  { value: 'auto', label: 'Follow the device' },
  { value: 'on', label: 'Always on' },
  { value: 'off', label: 'Always off' },
];

/** The few worth reaching for without leaving what you were doing. */
export function QuickSettings({
  settings,
  update,
  onOpenAll,
}: {
  settings: GameSettings;
  update: Update;
  onOpenAll: () => void;
}): JSX.Element {
  return (
    <div className="panel-body settings">
      <Toggle
        label="Tile grid"
        value={settings.showGrid}
        onChange={(showGrid) => update({ showGrid })}
      />
      <Toggle
        label="Highlight my tile"
        value={settings.highlightTile}
        onChange={(highlightTile) => update({ highlightTile })}
      />
      {/* Worth being one press away: somebody who finds the numbers busy wants
          them gone now rather than after a hunt through every page. */}
      <Toggle
        label="Experience drops"
        value={settings.showXpDrops}
        onChange={(showXpDrops) => update({ showXpDrops })}
      />
      <Toggle
        label="Minimap"
        value={settings.showMinimap}
        onChange={(showMinimap) => update({ showMinimap })}
      />
      <Slider
        label="Sensitivity"
        value={settings.sensitivity}
        min={0.3}
        max={2.5}
        step={0.1}
        format={(value) => value.toFixed(1)}
        onChange={(sensitivity) => update({ sensitivity })}
      />

      <button type="button" className="wide" onClick={onOpenAll}>
        All settings
      </button>
    </div>
  );
}

/** Everything, in a window that can be moved out of the way like any other. */
/** Which page of the settings is showing. */
type Page = 'display' | 'world' | 'sound' | 'controls' | 'keys' | 'layout';

const PAGES: readonly { page: Page; label: string }[] = [
  { page: 'display', label: 'Display' },
  { page: 'world', label: 'World' },
  { page: 'sound', label: 'Sound' },
  { page: 'controls', label: 'Controls' },
  { page: 'keys', label: 'Keys' },
  { page: 'layout', label: 'Layout' },
];

export function AllSettings({
  settings,
  update,
  bindings,
  onBind,
  onClose,
}: {
  settings: GameSettings;
  update: Update;
  /** What every action is bound to, including the inventory slots. */
  bindings: KeyBindings;
  onBind: (action: string, key: string) => void;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow('settings');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('settings', nudge, altHeld, {
    width: 340,
    height: 260,
  });
  const [page, setPage] = useState<Page>('display');

  return (
    <div
      className={`trade settings-window${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>All settings</span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="settings-tabs">
        {PAGES.map((entry) => (
          <button
            key={entry.page}
            type="button"
            className={page === entry.page ? 'settings-tab on' : 'settings-tab'}
            onClick={() => setPage(entry.page)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="panel-body settings settings-scroll">
        {page === 'display' ? (
          <>
            <Slider
              label="Resolution"
              value={settings.renderScale}
              min={0.5}
              max={1.5}
              step={0.05}
              format={(value) => `${Math.round(value * 100)}%`}
              onChange={(renderScale) => update({ renderScale })}
            />
            <Slider
              label="View distance"
              value={settings.viewDistance}
              min={50}
              max={settings.surveyCamera ? 700 : 220}
              step={10}
              format={(value) => `${value} tiles`}
              onChange={(viewDistance) => update({ viewDistance })}
            />
            <Toggle
              label="Survey camera"
              value={settings.surveyCamera}
              onChange={(surveyCamera) => update({ surveyCamera })}
            />
            <Toggle
              label="Name plates"
              value={settings.showNames}
              onChange={(showNames) => update({ showNames })}
            />
            <Choice
              label="Take roofs off"
              value={settings.roofs}
              options={ROOF_OPTIONS}
              onChange={(roofs) => update({ roofs })}
            />
          </>
        ) : null}

        {page === 'world' ? (
          <>
            <Toggle
              label="Tile grid"
              value={settings.showGrid}
              onChange={(showGrid) => update({ showGrid })}
            />
            <Toggle
              label="Highlight my tile"
              value={settings.highlightTile}
              onChange={(highlightTile) => update({ highlightTile })}
            />
            <Toggle
              label="Click marker"
              value={settings.showClickMarker}
              onChange={(showClickMarker) => update({ showClickMarker })}
            />
            <Toggle
              label="Day and night"
              value={settings.dayAndNight}
              onChange={(dayAndNight) => update({ dayAndNight })}
            />
            <Toggle
              label="Quest markers"
              value={settings.showQuestMarks}
              onChange={(showQuestMarks) => update({ showQuestMarks })}
            />
            <Toggle
              label="Ground clutter"
              value={settings.groundClutter}
              onChange={(groundClutter) => update({ groundClutter })}
            />
            <Toggle
              label="Game View as Panel"
              value={settings.gameViewPanel}
              onChange={(gameViewPanel) => update({ gameViewPanel })}
            />
            <Toggle
              label="Follow a new quest"
              value={settings.followNewQuests}
              onChange={(followNewQuests) => update({ followNewQuests })}
            />
            <Toggle
              label="Experience drops"
              value={settings.showXpDrops}
              onChange={(showXpDrops) => update({ showXpDrops })}
            />
            <Toggle
              label="Experience tracker"
              value={settings.xpTracker}
              onChange={(xpTracker) => update({ xpTracker })}
            />
            <p className="hint">
              Each skill you train this sitting, how fast, and how long to the next level.
            </p>
            <Toggle
              label="Friend requests"
              value={settings.takeFriendRequests}
              onChange={(takeFriendRequests) => update({ takeFriendRequests })}
            />
            <p className="hint">
              Off, nobody can ask to be your friend.
              <Explain>
                The request is refused where it is sent rather than hidden here, so whoever asked is
                told plainly instead of waiting on an answer that would never come.
              </Explain>
            </p>
            <Toggle
              label="Trade requests"
              value={settings.takeTradeRequests}
              onChange={(takeTradeRequests) => update({ takeTradeRequests })}
            />
            <p className="hint">Off, nobody can ask you to trade.</p>
            <Toggle
              label="Minimap"
              value={settings.showMinimap}
              onChange={(showMinimap) => update({ showMinimap })}
            />
            <Toggle
              label="Keep north at the top"
              value={settings.minimapNorthUp}
              onChange={(minimapNorthUp) => update({ minimapNorthUp })}
            />
            <p className="hint">
              Otherwise the map turns with the camera, with north marked on the rim.
            </p>
            <Toggle
              label="Keep me in the clear"
              value={settings.centreOnPlayer}
              onChange={(centreOnPlayer) => update({ centreOnPlayer })}
            />
            <p className="hint">
              Keeps your character out from behind the panels.
              <Explain>
                On a narrow screen this happens anyway, since there a panel really is in the way.
              </Explain>
            </p>

            <Slider
              label="Chat fades after"
              value={settings.chatFadeSeconds}
              min={0}
              max={60}
              step={1}
              format={(value) => (value === 0 ? 'never' : `${value}s`)}
              onChange={(chatFadeSeconds) => update({ chatFadeSeconds })}
            />

            <Slider
              label="Ask before dropping over"
              value={settings.confirmDropsOver}
              min={0}
              max={10000}
              step={50}
              format={(value) => (value === 0 ? 'never ask' : `${value.toLocaleString()} coins`)}
              onChange={(confirmDropsOver) => update({ confirmDropsOver })}
            />
          </>
        ) : null}

        {page === 'sound' ? (
          <>
            <Toggle
              label="Sound off"
              value={settings.muted}
              onChange={(muted) => update({ muted })}
            />
            <Slider
              label="Everything"
              value={settings.soundVolume}
              min={0}
              max={1}
              step={0.05}
              format={percent}
              onChange={(soundVolume) => update({ soundVolume })}
            />
            <Slider
              label="The world"
              value={settings.effectsVolume}
              min={0}
              max={1}
              step={0.05}
              format={percent}
              onChange={(effectsVolume) => update({ effectsVolume })}
            />
            <p className="hint">
              Fighting, footsteps, work and everything else happening round you.
            </p>
            <Slider
              label="Buttons and panels"
              value={settings.interfaceVolume}
              min={0}
              max={1}
              step={0.05}
              format={percent}
              onChange={(interfaceVolume) => update({ interfaceVolume })}
            />
            <Slider
              label="Background"
              value={settings.ambientVolume}
              min={0}
              max={1}
              step={0.05}
              format={percent}
              onChange={(ambientVolume) => update({ ambientVolume })}
            />
            <p className="hint">
              Wind, birds, the sea: whatever the place and the hour sound like.
            </p>
          </>
        ) : null}

        {page === 'controls' ? (
          <>
            <Toggle
              label="Panels float"
              value={settings.floatingPanels}
              onChange={(floatingPanels) => update({ floatingPanels })}
            />
            <p className="hint">
              Each panel becomes a window of its own.
              <Explain>
                Any number can be open at once and each stays where it is put. Off, they share the
                space above the buttons.
              </Explain>
            </p>

            <Toggle
              label="Name follows the pointer"
              value={settings.targetOnPointer}
              onChange={(targetOnPointer) => update({ targetOnPointer })}
            />
            <p className="hint">
              What the pointer is on is said beside the pointer.
              <Explain>
                Just above and to the right of it, wherever it goes, and out of the way the moment
                the pointer leaves the world. Off, it stays wherever it has been dragged.
              </Explain>
            </p>

            <Slider
              label="Tooltip after"
              value={settings.tooltipDelay}
              min={0}
              max={1200}
              step={20}
              format={(value) => (value === 0 ? 'at once' : `${value}ms`)}
              onChange={(tooltipDelay) => update({ tooltipDelay })}
            />

            <Choice
              label="Touch controls"
              value={settings.touch}
              options={TOUCH_OPTIONS}
              onChange={(touch) => update({ touch })}
            />
            <Toggle
              label="Invert look"
              value={settings.invertY}
              onChange={(invertY) => update({ invertY })}
            />
            <Slider
              label="Sensitivity"
              value={settings.sensitivity}
              min={0.3}
              max={2.5}
              step={0.1}
              format={(value) => value.toFixed(1)}
              onChange={(sensitivity) => update({ sensitivity })}
            />
          </>
        ) : null}

        {page === 'keys' ? <KeyPages bindings={bindings} onBind={onBind} /> : null}
        {page === 'layout' ? <LayoutSettings /> : null}
      </div>
      {grips}
    </div>
  );
}

/**
 * The keys, and the one being listened for.
 *
 * Pressing a row puts it into listening, and the next key goes to it. Escape
 * gives up and Backspace clears the binding, which are the two things somebody
 * halfway through changing their mind wants.
 */
function KeyPages({
  bindings,
  onBind,
}: {
  bindings: KeyBindings;
  onBind: (action: string, key: string) => void;
}): JSX.Element {
  const [listening, setListening] = useState<string | null>(null);

  useEffect(() => {
    if (!listening) return;

    const onKey = (event: KeyboardEvent): void => {
      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'Escape') {
        setListening(null);
        return;
      }

      // Something held is bound to one key, a modifier as happily as anything.
      if (HELD_ACTIONS.has(listening)) {
        onBind(listening, event.key === 'Backspace' ? '' : bareKeyOf(event));
        setListening(null);
        return;
      }

      // Held on their own they are not a binding, they are half of one.
      if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;

      onBind(listening, event.key === 'Backspace' ? '' : keyOf(event));
      setListening(null);
    };

    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [listening, onBind]);

  const slots = Object.entries(bindings)
    .map(([action, key]) => ({ slot: slotOf(action), action, key }))
    .filter((entry) => entry.slot !== null && entry.key)
    .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));

  return (
    <>
      <p className="hint">
        Click a key, then press the one you want.
        <Explain>Escape leaves it alone and Backspace clears it.</Explain>
      </p>

      {KEY_GROUPS.map((group) => (
        <div key={group.title}>
          <h3>{group.title}</h3>
          <div className="key-list">
            {group.actions.map((entry) => (
              <div key={entry.action} className="key-row">
                <span>
                  {entry.label}
                  {entry.about ? <small>{entry.about}</small> : null}
                </span>
                <button
                  type="button"
                  className={listening === entry.action ? 'key-bind listening' : 'key-bind'}
                  onClick={() => setListening(entry.action)}
                >
                  {listening === entry.action
                    ? 'press a key'
                    : readable(bindings[entry.action] ?? '')}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <h3>Bag slots</h3>
      {slots.length === 0 ? (
        <p className="hint">
          Middle click a slot in your bag to give it a key.
          <Explain>The key then does whatever a left click on that slot would do.</Explain>
        </p>
      ) : (
        <div className="key-list">
          {slots.map((entry) => (
            <div key={entry.action} className="key-row">
              <span>Slot {(entry.slot ?? 0) + 1}</span>
              <button
                type="button"
                className={listening === entry.action ? 'key-bind listening' : 'key-bind'}
                onClick={() => setListening(entry.action)}
              >
                {listening === entry.action ? 'press a key' : readable(entry.key)}
              </button>
            </div>
          ))}
        </div>
      )}

      <h3>Always</h3>
      <div className="key-list">
        {[
          ['Walk somewhere', 'Left click'],
          ['Menu', 'Right click'],
          ['Turn the camera', 'Drag with right, or the arrow keys'],
          ['Zoom', 'Wheel'],
          ['Close a window', 'Escape'],
        ].map(([what, key]) => (
          <div key={what} className="key-row">
            <span>{what}</span>
            <kbd>{key}</kbd>
          </div>
        ))}
      </div>
    </>
  );
}
